import { useEffect } from "react";

/**
 * @typedef {object} ProfileLoginPrefetchArgs
 * @property {string|null|undefined} uid
 *   Current Firebase Auth UID. When falsy, the hook is inert.
 * @property {Function} ensureProfileInfo
 *   Fetches or returns cached metadata freshness information for a user.
 * @property {Function} ensureProfile
 *   Fetches or returns cached metadata JSON for a user.
 * @property {Function} [getCachedProfileInfo]
 *   Optional synchronous cache lookup for freshness metadata.
 * @property {number} [ttlMs]
 *   Cache time-to-live in milliseconds for prefetch calls.
 * @property {number} [recertMs]
 *   Threshold in milliseconds beyond which profile JSON is worth warming.
 */

/**
 * useProfileLoginPrefetch
 * -----------------------
 * On login, prefetches:
 *  1) "info" (latest metadata timestamp) immediately (fast gate support)
 *  2) Profile JSON only when metadata looks stale enough to matter for
 *     recertification or edit flows
 *
 * This reduces perceived latency when the user immediately navigates to
 * the confirm metadata route.
 *
 * @param {ProfileLoginPrefetchArgs} args
 */
export default function useProfileLoginPrefetch({
  uid,
  ensureProfileInfo,
  ensureProfile,
  getCachedProfileInfo,
  ttlMs = 10 * 60 * 1000,
  recertMs = 3 * 30 * 24 * 60 * 60 * 1000, // pragmatic 90 days
}) {
  useEffect(() => {
    if (!uid) return;

    const t = window.setTimeout(() => {
      (async () => {
        const cachedInfo =
          typeof getCachedProfileInfo === "function"
            ? getCachedProfileInfo(uid)
            : null;

        const cachedAgeMs = cachedInfo?.latestMs
          ? Date.now() - cachedInfo.latestMs
          : Number.POSITIVE_INFINITY;
        const shouldWarmFromCache = cachedAgeMs >= recertMs;

        if (shouldWarmFromCache) {
          ensureProfile({ uid, ttlMs }).catch(() => {});
        }

        try {
          const info = await ensureProfileInfo({ uid, ttlMs });
          const latestAgeMs = info?.latestMs
            ? Date.now() - info.latestMs
            : Number.POSITIVE_INFINITY;

          if (latestAgeMs >= recertMs) {
            ensureProfile({ uid, ttlMs }).catch(() => {});
          }
        } catch {
          if (!shouldWarmFromCache) {
            ensureProfile({ uid, ttlMs }).catch(() => {});
          }
        }
      })();
    }, 0);

    return () => window.clearTimeout(t);
  }, [
    uid,
    ensureProfileInfo,
    ensureProfile,
    getCachedProfileInfo,
    ttlMs,
    recertMs,
  ]);
}
